import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRightIcon, StarIcon } from 'lucide-react';
import { providers } from '../data/providers';
import type { Slot } from '../types/slot';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { formatRtp, getCasinosForProvider, getProviderBySlug, getProviderStats, getSlotsByProvider } from '../utils/slots';
import { SlotTable } from '../components/ranking/SlotTable';
import { DemoModal } from '../components/DemoModal';
import { RealMoneyButton } from '../components/RealMoneyButton';
import { useSeo } from '../hooks/useSeo';
import { YEAR, breadcrumbLd, providerPageLd } from '../utils/seo';
export function ProviderDetail() {
  const {
    slug = ''
  } = useParams();
  const {
    showRealMoneyCtas
  } = useSiteSettings();
  const [demoSlot, setDemoSlot] = useState<Slot | null>(null);
  const close = useCallback(() => setDemoSlot(null), []);
  const provider = getProviderBySlug(slug);
  useSeo(provider ? {
    title: `${provider.name} Slots & Pokies in Nieuw-Zeeland (${YEAR}) | SlotGids`,
    description: `Alle ${provider.name} slots in de SlotGids Top 30, beoordeeld voor Nieuw-Zeeland. Vergelijk RTP, volatiliteit en max. winst, speel gratis demo’s en lees waarom ${provider.name} populair is bij Kiwi’s.`,
    path: `/providers/${provider.slug}`,
    jsonLd: [breadcrumbLd([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Providers',
      path: '/providers'
    }, {
      name: provider.name,
      path: `/providers/${provider.slug}`
    }]), providerPageLd(provider, getSlotsByProvider(provider.name))]
  } : {
    title: 'Provider niet gevonden | SlotGids',
    description: 'Deze provider staat niet in de SlotGids Top 30.',
    path: `/providers/${slug}`,
    noindex: true
  });
  if (!provider) {
    return <main className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6" data-id="element-407" data-mp="qfhgikf4wzs4">
        <h1 className="font-serif text-3xl font-semibold" data-id="element-408" data-mp="qhv82jalvtux">Deze provider staat niet in onze Top 30</h1>
        <p className="mt-3 text-muted" data-id="element-409" data-mp="10e6o9kdbh3un">Bekijk het overzicht van alle providers met slots in de ranglijst.</p>
        <Link to="/providers" className="mt-6 inline-flex h-11 items-center rounded-lg bg-fern px-5 font-semibold text-white" data-id="element-410" data-mp="vfdarry15cuh">
          Alle providers
        </Link>
      </main>;
  }
  const list = getSlotsByProvider(provider.name);
  const stats = getProviderStats(provider.name);
  const casinoList = getCasinosForProvider(provider.name).slice(0, 3);
  const others = providers.filter(p => p.slug !== provider.slug);
  const fmt = (n: number) => n.toLocaleString('nl-NL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
  const facts = [{
    label: 'Slots in Top 30',
    value: String(stats.count)
  }, {
    label: 'Hoogste positie',
    value: `#${stats.bestRank}`
  }, {
    label: 'Gem. RTP',
    value: formatRtp(stats.avgRtp)
  }, {
    label: 'Gem. SlotGids Score',
    value: fmt(stats.avgScore)
  }];
  return <main data-id="element-411" data-mp="20ea9r72qhl9m">
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6" data-id="element-412" data-mp="20esc8lfzovpz">
        <nav aria-label="Kruimelpad" className="flex items-center gap-1 text-sm text-muted" data-id="element-413" data-mp="1mmvm2mxvsvw8">
          <Link to="/" className="hover:text-ink" data-id="element-414" data-mp="1qo770f5t8sy3">Home</Link>
          <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-415" data-mp="1qogvfyb7is1o" />
          <Link to="/providers" className="hover:text-ink" data-id="element-416" data-mp="2eeurs10trd22">Providers</Link>
          <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-417" data-mp="2jdedq1ff4ouf" />
          <span className="truncate text-ink" aria-current="page" data-id="element-418" data-mp="2jaidu7k0fq3c">{provider.name}</span>
        </nav>

        <header className="mt-8 grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:gap-16" data-id="element-419" data-mp="25ffy6spd2mdc">
          <div data-id="element-420" data-mp="2aeatuv8fg5ev">
            <div className="flex items-center gap-4" data-id="element-421" data-mp="2ae0z6ucfta2u">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl font-serif text-xl font-semibold text-white" style={{
              backgroundColor: provider.color
            }} aria-hidden="true" data-id="element-422" data-mp="tx58cf05vw0n">
                {provider.name.replace(/[^A-Z]/g, '').slice(0, 2) || provider.name.slice(0, 2)}
              </span>
              <p className="text-sm text-muted" data-id="element-423" data-mp="14g39mcfawzir">
                Sinds {provider.founded} · {provider.headquarters}
              </p>
            </div>
            <h1 className="mt-5 font-serif text-4xl font-semibold leading-tight tracking-tight sm:text-5xl" data-id="element-424" data-mp="10dgfg4v5r2ie">
              {provider.name} slots in Nieuw-Zeeland
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted" data-id="element-425" data-mp="1s78r0w0w3zv6">{provider.intro}</p>
          </div>

          <dl className="grid grid-cols-2 gap-px self-end overflow-hidden rounded-2xl border border-line bg-line" data-id="element-426" data-mp="1o6ykni96r7fs">
            {facts.map(f => <div key={f.label} className="bg-surface px-5 py-5" data-id="element-427" data-mp="cpytipykqync">
                <dt className="text-xs text-muted" data-id="element-428" data-mp="7qlznl9acrb9">{f.label}</dt>
                <dd className="mt-1 font-serif text-2xl font-semibold tabular-nums" data-id="element-429" data-mp="7patxfwa59hi">{f.value}</dd>
              </div>)}
          </dl>
        </header>
      </div>

      <section aria-labelledby="provider-slots" className="mx-auto mt-16 max-w-7xl px-4 sm:px-6" data-id="element-430" data-mp="2oe3oe9jsglyg">
        <h2 id="provider-slots" className="font-serif text-3xl font-semibold tracking-tight" data-id="element-431" data-mp="2sdkvt8irzt69">
          {provider.name} in onze Top 30
        </h2>
        <p className="mt-2 text-muted" data-id="element-432" data-mp="2x771ggqiuwsk">Gerangschikt op positie in de algemene ranglijst.</p>
        <div className="mt-6" data-id="element-433" data-mp="326jl6sm3jnqx">
          <SlotTable slots={list} onDemo={setDemoSlot} data-id="element-434" data-mp="3730lel6klskd" />
        </div>
      </section>

      <div className="mx-auto mt-16 grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_360px]" data-id="element-435" data-mp="3c2mwop2u8yqj">
        <article className="space-y-10" data-id="element-436" data-mp="3g3yew9jduhop">
          <section aria-labelledby="provider-markets" className="grid gap-8 sm:grid-cols-2" data-id="element-437" data-mp="3g3onqimlq4hf">
            <h2 id="provider-markets" className="sr-only" data-id="element-438" data-mp="1hpx2e68k5nim">Populariteit per markt</h2>
            <div data-id="element-439" data-mp="1lr8klqp3r6gs">
              <h3 className="font-semibold" data-id="element-440" data-mp="1lqpdz64wk4i2">In Nieuw-Zeeland</h3>
              <p className="mt-2 leading-relaxed text-muted" data-id="element-441" data-mp="2p0l2w4o91xgs">{provider.nzNote}</p>
            </div>
            <div data-id="element-442" data-mp="2k2ji9fb6dq37">
              <h3 className="font-semibold" data-id="element-443" data-mp="2k31p15vgrmpd">Bij Nederlandstalige spelers</h3>
              <p className="mt-2 leading-relaxed text-muted" data-id="element-444" data-mp="2y2aubai4swek">{provider.nlNote}</p>
            </div>
          </section>

          <section aria-labelledby="provider-strengths" data-id="element-445" data-mp="2tz4ou52uf995">
            <h2 id="provider-strengths" className="font-serif text-2xl font-semibold" data-id="element-446" data-mp="37zyzkudd5maz">Waar {provider.name} in uitblinkt</h2>
            <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-447" data-mp="331dx0oan2qjd">
              {provider.strengths.map(s => <li key={s} className="py-3 font-medium" data-id="element-448" data-mp="3hogyt75zvlci">{s}</li>)}
            </ul>
          </section>

          <section aria-labelledby="provider-facts" data-id="element-449" data-mp="3crh0xjjatowh">
            <h2 id="provider-facts" className="font-serif text-2xl font-semibold" data-id="element-450" data-mp="3qpx4j1lv6zae">Feiten</h2>
            <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2" data-id="element-451" data-mp="3lrvpcrrk7k47">
              <div data-id="element-452" data-mp="3lpiz7rbne3g2">
                <dt className="text-sm text-muted" data-id="element-453" data-mp="9814onu34uzm">Kenmerkende mechaniek</dt>
                <dd className="font-medium" data-id="element-454" data-mp="95yg888kz9x0">{provider.signature}</dd>
              </div>
              <div data-id="element-455" data-mp="e4rsjxc4suvl">
                <dt className="text-sm text-muted" data-id="element-456" data-mp="7382qlz7pr2">Licenties</dt>
                <dd className="font-medium" data-id="element-457" data-mp="6ti330xlild">{provider.licenses}</dd>
              </div>
              <div data-id="element-458" data-mp="473m4a55xyxl">
                <dt className="text-sm text-muted" data-id="element-459" data-mp="4arhzfy2yxfh">Opgericht</dt>
                <dd className="font-medium" data-id="element-460" data-mp="3mlmjqfjkgqtu">{provider.founded}</dd>
              </div>
              <div data-id="element-461" data-mp="3rdntnut9i5gp">
                <dt className="text-sm text-muted" data-id="element-462" data-mp="3re5znj5q7y1t">Hoofdkantoor</dt>
                <dd className="font-medium" data-id="element-463" data-mp="3cp00twasw9ug">{provider.headquarters}</dd>
              </div>
            </dl>
          </section>
        </article>

        <aside className="lg:sticky lg:top-32 lg:self-start" data-id="element-464" data-mp="3hnuk0zcok0f3">
          {showRealMoneyCtas && casinoList.length > 0 ? <section aria-labelledby="provider-casinos" className="rounded-2xl border border-line bg-surface" data-id="element-465" data-mp="33q5zjsmiyvak">
              <div className="border-b border-line px-5 py-4" data-id="element-466" data-mp="3o4weoir7kszh">
                <h2 id="provider-casinos" className="font-serif text-xl font-semibold" data-id="element-467" data-mp="3o44qvu3urzjr">
                  Casino’s met {provider.name}
                </h2>
                <p className="mt-1 text-xs text-muted" data-id="element-468" data-mp="3e5yqjgxp31h0">Advertentie · 18+ · Voorwaarden van toepassing</p>
              </div>
              <ol className="divide-y divide-line" data-id="element-469" data-mp="3a4d71cqnnoxu">
                {casinoList.map(c => <li key={c.id} className="flex items-center gap-3 px-5 py-4" data-id="element-470" data-mp="356ttkf1s7g6y">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg font-serif font-semibold text-white" style={{
                backgroundColor: c.color
              }} aria-hidden="true" data-id="element-471" data-mp="30f1ybr7okmah">
                      {c.initials}
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-472" data-mp="2merayy5kxpu2">
                      <p className="truncate font-semibold" data-id="element-473" data-mp="2hg7j6h4b1jts">{c.name}</p>
                      <p className="flex items-center gap-1 truncate text-xs text-muted" data-id="element-474" data-mp="wyf6edz6rodq">
                        <StarIcon className="h-3 w-3 fill-oranje text-oranje" aria-hidden="true" data-id="element-475" data-mp="x0rpiugok4sd" />
                        {c.rating} · {c.bonus}
                      </p>
                    </div>
                    <RealMoneyButton casino={c} size="sm" label="Bekijk" className="shrink-0" data-id="element-476" data-mp="15xaejvlsj8jo" />
                  </li>)}
              </ol>
            </section> : <div className="rounded-2xl border border-line bg-surface p-5" data-id="element-477" data-mp="e5ujrea2yfid">
              <h2 className="font-serif text-xl font-semibold" data-id="element-478" data-mp="ia26j5wz9ldy">Gratis proberen</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-479" data-mp="n7khf1uk2aol">
                Elke {provider.name}-slot in de lijst heeft een demoknop. Speel met speelgeld voordat je beslist.
              </p>
            </div>}

          <nav aria-labelledby="other-providers" className="mt-8" data-id="element-480" data-mp="3qn9izv3mmzhj">
            <h2 id="other-providers" className="text-sm font-semibold" data-id="element-481" data-mp="3vn49hxjhpn22">Andere providers</h2>
            <ul className="mt-3 flex flex-wrap gap-2" data-id="element-482" data-mp="gnsf05kfwgqh">
              {others.map(p => <li key={p.slug} data-id="element-483" data-mp="kpnpo0o9g6gx">
                  <Link to={`/providers/${p.slug}`} className="inline-flex h-8 items-center whitespace-nowrap rounded-full border border-line bg-surface px-3 text-sm transition-colors duration-150 hover:border-ink/30" data-id="element-484" data-mp="kom2wyxvtvjd">
                    {p.name}
                  </Link>
                </li>)}
            </ul>
          </nav>
        </aside>
      </div>

      <DemoModal slot={demoSlot} onClose={close} data-id="element-485" data-mp="6z0qy8ii2gku" />
    </main>;
}